<template>
  <b-container>
    <SiteTabs />
    <b-row>
      <b-col class="text-center all-caps">
        <h1>
          Songback <br>
          Privacy Policy <br>
          Last Updated: July 6, 2018
        </h1>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <p>
          This privacy policy (“<strong>Policy</strong>”) describes how Songback, LLC and its related companies (“<strong>Company</strong>”) collect, use and share personal information of consumer users of this Site (the “<strong>Site</strong>”). This Policy also applies to any of our other mobile applications or websites that post this Policy. This Policy does not apply to websites or mobile applications that post different statements.
        </p>

        <h3 class="all-caps">WHAT WE COLLECT</h3>

        <p>
          We get information about you in a range of ways.
        </p>

        <div class="py-2 pl-4">
          <p>
            <strong>Information You Give Us.</strong> We collect your‎ name, postal address, email address, phone number, username, password, demographic information (such as your gender and occupation), date of birth, as well as other information you directly give us on our Site. Content that you share either with the Company or with other Site users, including without limitation, emails, reviews, photographs, videos, comments, or music is collected by us.
          </p>
          <p>
            <strong>Information We Get From Others.</strong> We may get information about you from other sources. We may add this to information we get from this Site.
          </p>
          <p>
            <strong>Information Automatically Collected.</strong> We automatically log information about you and your device. For example, when visiting our Site, we log your IP address, computer operating system type, browser type, device information, device event information (e.g. crashes), browser language, pages you viewed, how long you spent on a page, access times and information about your use of and actions on our Site.
          </p>
          <p>
            <strong>Geo-Location Information.</strong> We may request access or permission to and track location-based information from your device, either continuously or while you are using our Site, to provide location-based services including without limitation advertising and personalizing content. If you wish to change our access or permissions, you may do so in your device’s settings.
          </p>
          <p>
            <strong>Cookies.</strong> We may log information using "cookies." Cookies are small data files stored on your computer. We may use both session Cookies (which expire once you close the Site) and persistent Cookies (which stay on your computer or device until you delete them) to provide you with a more personal and interactive experience on our Site. This type of information is collected to make the Site more useful to you and to tailor the experience with us to meet your special interests and needs.
          </p>
          <p>
            <strong>Third-Party Data.</strong> We may automatically collect and integrate personal data from third-party APIs into the Site. A list of the APIs integrated into the Site and the information that is collected follows:
          </p>

          <div class="py-2 pl-4">
            <p>
              <strong>Facebook:</strong> Automated login information as well as email and public profile information (their data (including privacy) policy can be found at <a href="https://www.facebook.com/full_data_use_policy" target="_blank">https://www.facebook.com/full_data_use_policy</a>);
            </p>
            <p>
              <strong>Google Accounts:</strong> Automated login information as well as email and public profile information (their privacy policy can be found at <a href="https://policies.google.com/privacy?hl=en&gl=us" target="_blank">https://policies.google.com/privacy?hl=en&gl=us</a>);
            </p>
            <p>
              <strong>DaCast:</strong> To display and stream live or saved video (their privacy policy can be found at <a href="https://www.dacast.com/privacy-policy/" target="_blank">https://www.dacast.com/privacy-policy/</a>);
            </p>
            <p>
              <strong>Stripe:</strong> To process payments including credit card information (only limited information is shared with Songback so as to process receipts of payment) (their privacy policy can be found at <a href="https://stripe.com/us/privacy/" target="_blank">https://stripe.com/us/privacy/</a> and <a href="https://stripe.com/privacy-shield-policy" target="_blank">https://stripe.com/privacy-shield-policy</a> and <a href="https://stripe.com/guides/general-data-protection-regulation" target="_blank">https://stripe.com/guides/general-data-protection-regulation</a>);
            </p>
            <p>
              <strong>Pusher:</strong> To allow users to engage in live chat and receive real-time notifications through the Site (their privacy policy can be found at <a href="https://pusher.com/legal/privacy-policy" target="_blank">https://pusher.com/legal/privacy-policy</a> and <a href="https://pusher.com/legal/data-protection" target="_blank">https://pusher.com/legal/data-protection</a>)
            </p>
          </div>

          <p>
            <strong>Google Analytics.</strong> The Site uses Google Analytics, a web analysis service of Google, Inc., 1600 Amphitheatre Parkway, Mountain View, CA 94043 USA, www.google.com (“Google Analytics” or “Google”). Google Analytics employs cookies that are stored to your computer in order to facilitate an analysis of your use of the Site. The information generated by these cookies, such as time, place and frequency of your visits to the Site, including your IP address, is transmitted to Google’s location in the US and stored there.
          </p>
          <p>
            Google abbreviates and thereby anonymizes your IP address before transferring it from EU/EEA member states. Google uses this information to analyze your use of the Site, to compile reports for us on internet activity and to provide other services relating to the Site.
          </p>
          <p>
            Google Analytics also offers a deactivation add-on for most current browsers that provides you with more control over what data Google can collect on websites you access. However, the browser deactivation add-on offered by Google Analytics does not prevent information from being transmitted to us or to other web analysis services we may engage.
          </p>
          <p>
            Google Analytics also uses electronic images known as web beacons (sometimes called single pixel .gifs) and are used along with cookies to compile aggregated statistics to analyze how our site is used.
          </p>
          <p>
            For the cases in which personal data is transferred to the US, Google has self-certified pursuant to the EU-US Privacy Shield (https://www.privacyshield.gov/EU-US-Framework).
          </p>
        </div>

        <h3 class="all-caps">USE OF PERSONAL INFORMATION</h3>

        <p>
          We use your personal information as follows:
        </p>

        <ul>
          <li>We use your personal information to operate, maintain, and improve our products and services.</li>
          <li>We use your personal information to identify you as the person who uploaded User Content (as defined in the Terms of Use).</li>
          <li>We use your personal information to analyze research conducted on our Site, develop new features, and troubleshoot issues on the Site.</li>
          <li>We use the date of your birth to enable age restrictions on the Site.</li>
          <li>We use your personal information to process and deliver contest entries and rewards.</li>
          <li>We use your personal information to respond to comments and questions and provide customer service.</li>
          <li>We use your personal information to send information including confirmations, invoices, technical notices, updates, security alerts, and support and administrative messages.</li>
          <li>We use your personal information to communicate about discount offerings, promotions, upcoming events, and other news about products and services offered by us and our selected partners.</li>
          <li>We use your personal information to link or combine user information with other personal information.</li>
          <li>We use your personal information to protect, investigate, and deter against fraudulent, unauthorized, or illegal activity.</li>
          <li>We use your personal information to provide and deliver products and services customers request.</li>
          <li>We use your personal information to customize content, including advertising and direct, targeted-marketing campaigns on and off the Site.</li>
          <li>We use your personal information to measure the success of targeted, direct-marketing campaigns and content customized to your use of the Site and of third-party websites and mobile applications.</li>
        </ul>

        <h3>SHARING OF PERSONAL INFORMATION</h3>

        <p>
          We may share personal information as follows:
        </p>

        <ul>
          <li>We may share personal information with your consent. For example, you may let us share personal information with others for their own marketing uses. Those uses will be subject to their privacy policies.</li>
          <li>We may share personal information when we do a business deal, or negotiate a business deal, involving the sale or transfer of all or a part of our business or assets. These deals can include any merger, financing, acquisition, or bankruptcy transaction or proceeding.</li>
          <li>We may share personal information for legal, protection, and safety purposes.</li>
          <ul>
            <li>We may share information to comply with laws.</li>
            <li>We may share information to respond to lawful requests and legal processes.</li>
            <li>We may share information to protect the rights and property of Songback, LLC, our agents, customers, and others. This includes enforcing our agreements, policies, and terms of use.</li>
            <li>We may share information in an emergency. This includes protecting the safety of our employees and agents, our customers, or any person.</li>
          </ul>
          <li>We may share personal information with those who need it to do work for us.</li>
          <li>We may share personal information with third party services including ad-networks and exchanges that allow us to target ads to you based on the content you are viewing on our Site or on a third-party website or mobile application.</li>
        </ul>

        <p>
          We may also share aggregated and/or anonymized data with others for their own uses.
        </p>

        <h3 class="all-caps">INFORMATION CHOICES AND CHANGES</h3>

        <p>
          Our marketing emails tell you how to “opt-out.” If you opt out, we may still send you non-marketing emails. Non-marketing emails include emails about your accounts and our business dealings with you.
        </p>
        <p>
          You may send requests about personal information to our Contact Information below. You can request to change contact choices, opt-out of our sharing with others, and update your personal information.
        </p>
        <p>
          You can typically remove and reject cookies from our Site with your browser settings. Many browsers are set to accept cookies until you change your settings. If you remove or reject our cookies, it could affect how our Site works for you.
        </p>
        <p>
          User Content (as defined in the Terms of Use) is deemed not to be personal information and the Site cannot guarantee that User Content will remain private. However, when your account is terminated or the User Content is removed from the Site, either by us or by you, it will not be shared with third-parties.
        </p>

        <h3 class="all-caps">YOUR RIGHTS-EEA RESIDENTS</h3>

        <p>
          Pursuant to the European Union’s (EU) General Data Protection Regulation (GDPR), those users in the European Economic Area (EEA), which access this Site are hereby informed that our servers are located in the United States. If you access the Site outside the United States, the information that is transferred to, stored and processed by us in our facilities and by third parties with whom we may share your personal information may be in countries that do not have data protection or other laws as comprehensive as those in your country. We will, however, take all necessary measures to protect your personal information and inform you of the following rights:
        </p>

        <ul>
          <li>The right to be informed</li>
          <ul>
            <li>This means if this Site obtains personal information about a user, it will immediately inform them of the collection of such data.</li>
            <li>It also means that if this Site obtains personal information about a user from a third-party, it will inform that user of the acquisition of such data within one month, unless obtaining such data would involve a disproportionate effort.</li>
          </ul>
          <li>The right to access</li>
          <ul>
            <li>This includes two prongs: (1) the Site will inform any user who requests such information whether any of the user’s personal data is being processed and (2) if such personal data is being processed, information about the use and purpose of the data processed.</li>
          </ul>
          <li>The right to rectification</li>
          <ul>
            <li>Any user may use the contact information herein to request inaccurate or incomplete personal data be corrected and the Site will, within one month, respond to such a request.</li>
          </ul>
          <li>The right to erasure (be forgotten)</li>
          <ul>
            <li>Within one month of the request, the Site will respond to a request for erasure of personal data.</li>
            <li>When responding to requests for erasure the Site will determine whether the purpose for which personal data is used is no longer necessary; whether the user withdraws consent to use personal data; and whether processing of the relevant personal data is required to comply with any other legal obligation.</li>
          </ul>
          <li>The right to restrict processing</li>
          <ul>
            <li>This right entails the Site temporarily moving the relevant personal data to another processing system, making it unavailable to users or temporarily removing published data from the Site</li>
          </ul>
          <li>The right to data portability</li>
          <ul>
            <li>Users may either receive a copy of their personal data stored on the Site’s servers or have such personal data transmitted to another controller</li>
          </ul>
          <li>The right to object </li>
          <ul>
            <li>Users have the absolute right to object to the processing of their data for marketing purposes.</li>
            <li>Users may also object to the processing of their data so long as they provide specific reasons for their objection; the Site may respond to those objections within one month.</li>
          </ul>
          <li>The right of automated decision-making in profiling</li>
          <ul>
            <li>The Site will undertake to consider and document the reasons for undertaking or not undertaking a Data Protection Impact Assessment (DPIA) whenever user personal data will be used in any automated-decision making on the Site.</li>
          </ul>
        </ul>

        <p>
          <strong>CONTACT INFORMATION.</strong> We welcome your comments or questions about this privacy policy. You may also contact us at our address: <br>
          <br>
          Songback, LLC <br>
          P.O. Box 18011 <br>
          Irvine, CA 92623 <br>
        </p>

        <p>
          <strong>CHANGES TO THIS PRIVACY POLICY.</strong> We may change this privacy policy. If we make any changes, we will change the Last Updated date above.
        </p>

      </b-col>
    </b-row>
  </b-container>
</template>

<style scoped lang="scss">
  h1 {
    margin-top: 2em;
    margin-bottom: 2em;
    font-size: 1.5rem;
  }

  h2 {
    margin-top: 1.5em;
    font-size: 1.5rem;
  }

  h3 {
    margin-top: 1.5em;
    font-size: 1.2rem;
  }

  .all-caps {
    text-transform: uppercase;
  }

  span.spacer:after {
    content: ' ';
    margin-right: 3em;
  }

  small.spacer:after {
    content: ' ';
    margin-right: 2em;
  }
</style>

<script>
import SiteTabs from '@/components/widgets/SiteTabs'

export default {
  components: {
    SiteTabs
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/site/PrivacyPolicy.vue